import type { VisualPayload, GameEvent, NewsItem } from "../../types";
import { isSafeUrl } from "../../lib/safeUrl";
import CardShell from "./_CardShell";

interface Props { payload: VisualPayload }

// Event times come in two flavours (docs/decisions/0002):
//   is_local_time = true  → wall-clock wherever the player is (Raid Hour is
//                           6pm in every zone). Format in UTC so the stored
//                           wall-clock components are shown unchanged.
//   is_local_time = false → a single global instant (GO Battle League resets,
//                           Wild Area). Format in the viewer's own zone.
export function formatEventWhen(iso: string | null | undefined, isLocalTime: boolean): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const tz: Intl.DateTimeFormatOptions = isLocalTime ? { timeZone: "UTC" } : {};
  return d.toLocaleString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    ...tz,
  });
}

function EventRow({ ev, mode }: { ev: GameEvent; mode: "active" | "upcoming" }) {
  const when =
    mode === "active"
      ? ev.ends_at
        ? `until ${formatEventWhen(ev.ends_at, ev.is_local_time)}`
        : ""
      : formatEventWhen(ev.starts_at, ev.is_local_time);
  const name = isSafeUrl(ev.link) ? (
    <a
      href={ev.link}
      target="_blank"
      rel="noopener noreferrer"
      className="text-white/90 hover:text-amber-300 transition-colors"
    >
      {ev.name}
    </a>
  ) : (
    <span className="text-white/90">{ev.name}</span>
  );
  return (
    <li className="flex flex-col gap-0.5 text-xs border-l-2 border-amber-500/25 pl-2.5 py-1 min-w-0">
      <div className="flex flex-wrap items-baseline gap-x-2 min-w-0">
        <span className="font-medium break-words min-w-0">{name}</span>
        {ev.heading && ev.heading !== ev.name && (
          <span className="text-[10px] uppercase tracking-wider text-white/40">{ev.heading}</span>
        )}
      </div>
      {ev.highlight && <p className="text-white/65 break-words">{ev.highlight}</p>}
      {when && (
        <p className="text-white/45">
          {when}
          {ev.is_local_time ? " local time" : ""}
        </p>
      )}
    </li>
  );
}

export default function EventsCard({ payload }: Props) {
  const sections = payload.sections ?? [];
  const active = (sections.find((s) => s.section === "events_active")?.events as GameEvent[] | undefined) ?? [];
  const upcoming = (sections.find((s) => s.section === "events_upcoming")?.events as GameEvent[] | undefined) ?? [];
  const news = (sections.find((s) => s.section === "events_news")?.news as NewsItem[] | undefined) ?? [];

  return (
    <CardShell
      title={payload.title}
      source={payload.data_source}
      timestamp={payload.data_timestamp}
      sourceUrl={payload.source_url}
      shareUrl={payload.share_url}
      snapshotPayload={payload.snapshot_eligible ? payload : undefined}
      accent="border-amber-500/30"
    >
      {active.length === 0 && upcoming.length === 0 && news.length === 0 && (
        <p className="text-sm text-white/55 py-2">No events on file for the next two weeks.</p>
      )}

      {active.length > 0 && (
        <section className="mb-4">
          <h4 className="text-[11px] font-semibold uppercase tracking-widest mb-2 text-emerald-300">
            Happening now ({active.length})
          </h4>
          <ul className="space-y-2">
            {active.map((ev) => <EventRow key={ev.event_id} ev={ev} mode="active" />)}
          </ul>
        </section>
      )}

      {upcoming.length > 0 && (
        <section className="mb-4">
          <h4 className="text-[11px] font-semibold uppercase tracking-widest mb-2 text-sky-300">
            Coming up ({upcoming.length})
          </h4>
          <ul className="space-y-2">
            {upcoming.map((ev) => <EventRow key={ev.event_id} ev={ev} mode="upcoming" />)}
          </ul>
        </section>
      )}

      {news.length > 0 && (
        <section>
          <h4 className="text-[11px] font-semibold uppercase tracking-widest mb-2 text-white/55">
            Latest news
          </h4>
          <ul className="space-y-1.5">
            {news.map((n) => (
              <li key={n.url} className="text-xs break-words">
                {isSafeUrl(n.url) ? (
                  <a
                    href={n.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-white/75 hover:text-amber-300 transition-colors"
                  >
                    {n.title}
                  </a>
                ) : (
                  <span className="text-white/75">{n.title}</span>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}
    </CardShell>
  );
}
